import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import type {
  MannequinMetadata,
  MannequinOverlay,
  MannequinOutputSurface,
  MannequinSurfaceLayerVisibility,
  MannequinSurfaceResponse,
} from "../api/mannequin";
import { tailorMannequinAxis } from "../features/thread-v5/tailorMannequinDemoContract";
export type MannequinViewMode = "single" | "all";
export type MannequinSurfaceViewState = {
  yaw: number;
  pitch: number;
  zoom: number;
  panX: number;
  panY: number;
};
type Props = {
  metadata: MannequinMetadata;
  outputs: string[];
  surface: MannequinSurfaceResponse;
  viewMode: MannequinViewMode;
  viewState?: MannequinSurfaceViewState;
  onViewStateChange?: (viewState: MannequinSurfaceViewState) => void;
  /** Visibility of semantic surface layers. Omitted values remain visible. */
  layers?: Partial<MannequinSurfaceLayerVisibility>;
  /** Right-click "Why this point?" on a marker that carries a SamplePoint id
   * (overlay.ids). Without it, right-click just suppresses the browser menu. */
  onPointContextMenu?: (pointId: string, clientX: number, clientY: number) => void;
};
type Panel = {
  output: string;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  group: THREE.Group;
};
const LAYOUT = { width: 2.55, depth: 1.62, lift: 1.34 };
export const DEFAULT_MANNEQUIN_VIEW: MannequinSurfaceViewState = { yaw: -0.38, pitch: 0.78, zoom: 1, panX: 0, panY: 0 };
function formatNumber(value: number | undefined): string {
  if (value === undefined || Number.isNaN(value)) return "n/a";
  return Math.abs(value) >= 100 ? value.toFixed(1) : value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}
function normValue(value: number, min: number, max: number): number {
  return Math.max(0, Math.min(1, (value - min) / Math.max(1.0e-12, max - min)));
}
function zForValue(value: number, range: [number, number]): number {
  return (normValue(value, range[0], range[1]) - 0.5) * LAYOUT.lift;
}
function fractionInRange(value: number, min: number, max: number): number {
  return Math.max(0, Math.min(1, (value - min) / Math.max(1.0e-12, max - min)));
}
function finiteOrFallback(value: number, fallback: number): number {
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}
function sequentialColor(t: number): THREE.Color {
  const stops: Array<[number, [number, number, number]]> = [
    [0, [36, 73, 158]],
    [0.23, [31, 129, 184]],
    [0.48, [24, 170, 139]],
    [0.72, [178, 204, 73]],
    [1, [226, 118, 43]],
  ];
  const clamped = Math.max(0, Math.min(1, t));
  for (let index = 0; index < stops.length - 1; index += 1) {
    const [a, ca] = stops[index]!;
    const [b, cb] = stops[index + 1]!;
    if (clamped <= b) {
      const local = (clamped - a) / Math.max(1.0e-12, b - a);
      return new THREE.Color(
        (ca[0] + (cb[0] - ca[0]) * local) / 255,
        (ca[1] + (cb[1] - ca[1]) * local) / 255,
        (ca[2] + (cb[2] - ca[2]) * local) / 255,
      );
    }
  }
  const last = stops[stops.length - 1]![1];
  return new THREE.Color(last[0] / 255, last[1] / 255, last[2] / 255);
}
function uncertaintyColor(t: number): THREE.Color {
  const clamped = Math.max(0, Math.min(1, t));
  const styles = getComputedStyle(document.documentElement);
  const token = (name: string) => {
    const value = styles.getPropertyValue(name).trim();
    return value ? new THREE.Color(value) : new THREE.Color(0.5, 0.5, 0.5);
  };
  const certain = token("--v5-primary");
  const middle = token("--v5-amber");
  const uncertain = token("--v5-warn");
  return clamped <= 0.62
    ? certain.lerp(middle, clamped / 0.62)
    : middle.lerp(uncertain, (clamped - 0.62) / 0.38);
}
function surfaceGridPoint(
  data: MannequinSurfaceResponse,
  col: number,
  row: number,
  cols: number,
  rows: number,
  z: number,
  zRange: [number, number],
  zOffset = 0,
): THREE.Vector3 {
  const xv = data.x_values[col];
  const yv = data.y_values[row];
  const xFraction = typeof xv === "number"
    ? fractionInRange(xv, data.x_range[0], data.x_range[1])
    : col / Math.max(1, cols - 1);
  const yFraction = typeof yv === "number"
    ? fractionInRange(yv, data.y_range[0], data.y_range[1])
    : row / Math.max(1, rows - 1);
  return new THREE.Vector3(
    -LAYOUT.width / 2 + xFraction * LAYOUT.width,
    -LAYOUT.depth / 2 + yFraction * LAYOUT.depth,
    zForValue(finiteOrFallback(z, zRange[0]), zRange) + zOffset,
  );
}
function makeSurfaceGeometry(
  data: MannequinSurfaceResponse,
  z: number[][],
  zRange: [number, number],
  colourValues = z,
  colourRange = zRange,
  colourBy: "response" | "uncertainty" = "response",
): THREE.BufferGeometry {
  const rows = z.length;
  const cols = z[0]?.length ?? 0;
  const positions: number[] = [];
  const colors: number[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const value = finiteOrFallback(z[row]?.[col] ?? zRange[0], zRange[0]);
      const colourValue = finiteOrFallback(colourValues[row]?.[col] ?? colourRange[0], colourRange[0]);
      const colourFraction = normValue(colourValue, colourRange[0], colourRange[1]);
      const color = colourBy === "uncertainty" ? uncertaintyColor(colourFraction) : sequentialColor(colourFraction);
      const point = surfaceGridPoint(data, col, row, cols, rows, value, zRange);
      positions.push(point.x, point.y, point.z);
      colors.push(color.r, color.g, color.b);
    }
  }
  const indices: number[] = [];
  for (let row = 0; row < rows - 1; row += 1) {
    for (let col = 0; col < cols - 1; col += 1) {
      const a = row * cols + col;
      const b = a + 1;
      const c = a + cols;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}
function addSurfaceMesh(
  group: THREE.Group,
  data: MannequinSurfaceResponse,
  surface: MannequinOutputSurface,
  zRange: [number, number],
  opacity = 1,
  showUncertainty = true,
): void {
  const colourBy = showUncertainty ? surface.colour_by ?? "response" : "response";
  group.add(
    new THREE.Mesh(
      makeSurfaceGeometry(
        data,
        surface.z,
        zRange,
        colourBy === "uncertainty" ? surface.colour_values ?? surface.z : surface.z,
        colourBy === "uncertainty" ? surface.colour_range ?? zRange : zRange,
        colourBy,
      ),
      new THREE.MeshStandardMaterial({
        vertexColors: true,
        roughness: 0.76,
        metalness: 0,
        side: THREE.DoubleSide,
        transparent: opacity < 1,
        opacity,
      }),
    ),
  );
}
function addTintedSurfaceMesh(group: THREE.Group, data: MannequinSurfaceResponse, z: number[][], zRange: [number, number], color: string, opacity: number): void {
  const mesh = new THREE.Mesh(
    makeSurfaceGeometry(data, z, zRange),
    new THREE.MeshStandardMaterial({
      color,
      transparent: true,
      opacity,
      roughness: 0.82,
      metalness: 0,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  mesh.renderOrder = 4;
  group.add(mesh);
}
function addWireGrid(
  group: THREE.Group,
  data: MannequinSurfaceResponse,
  z: number[][],
  zRange: [number, number],
  color = "#2b3b38",
  opacity = 0.62,
  zOffset = 0.008,
): void {
  const rows = z.length;
  const cols = z[0]?.length ?? 0;
  const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity, depthTest: false });
  const makePoint = (col: number, row: number) =>
    surfaceGridPoint(data, col, row, cols, rows, finiteOrFallback(z[row]?.[col] ?? zRange[0], zRange[0]), zRange, zOffset);
  const rowStep = Math.max(1, Math.floor(rows / 9));
  const colStep = Math.max(1, Math.floor(cols / 9));
  for (let row = 0; row < rows; row += rowStep) {
    group.add(new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(Array.from({ length: cols }, (_, col) => makePoint(col, row))),
      material.clone(),
    ));
  }
  for (let col = 0; col < cols; col += colStep) {
    group.add(new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(Array.from({ length: rows }, (_, row) => makePoint(col, row))),
      material.clone(),
    ));
  }
}
function zValues(z: number[][]): number[] {
  return z.flat().filter((value) => Number.isFinite(value));
}
function displayRange(surface: MannequinOutputSurface): [number, number] {
  const values = [
    ...zValues(surface.z),
    ...surface.overlays.flatMap((overlay) => overlay.z.filter((value) => Number.isFinite(value))),
    // Monte-Carlo member extremes must stretch the drawn range too — the
    // widest clouds sit at the response extremes (stall band), and a clamped
    // member flattens into the box wall, silently understating uncertainty.
    ...surface.overlays.flatMap((overlay) =>
      (overlay.mc ?? []).flatMap((members) => members ?? []).filter((value) => Number.isFinite(value))),
    ...(surface.comparison_surfaces ?? []).flatMap((comparison) => zValues(comparison.z)),
  ];
  if (!values.length) return surface.z_range;
  const min = Math.min(...values);
  const max = Math.max(...values);
  return max > min ? [min, max] : [min - 0.5, max + 0.5];
}
function comparisonLayer(comparison: NonNullable<MannequinOutputSurface["comparison_surfaces"]>[number]): "prior" | "truth" | undefined {
  return comparison.layer
    ?? (comparison.name === "Low-fi prior" ? "prior" : comparison.name === "Mannequin oracle" ? "truth" : undefined);
}
function overlayLayer(overlay: MannequinOverlay): "oracle-target" | "samples" {
  return overlay.layer ?? (/oracle|truth target/i.test(overlay.name) ? "oracle-target" : "samples");
}
function addComparisonSurfaces(
  group: THREE.Group,
  data: MannequinSurfaceResponse,
  surface: MannequinOutputSurface,
  zRange: [number, number],
  layers: Partial<MannequinSurfaceLayerVisibility>,
): void {
  for (const comparison of surface.comparison_surfaces ?? []) {
    const layer = comparisonLayer(comparison);
    if (layer && layers[layer] === false) continue;
    const color = comparison.color ?? "#ff6f4f";
    addTintedSurfaceMesh(group, data, comparison.z, zRange, color, comparison.opacity ?? 0.18);
    addWireGrid(group, data, comparison.z, zRange, color, comparison.wireOpacity ?? 0.82, 0.032);
  }
}
function makeTextSprite(text: string, panelCount: number, tone: "axis" | "tick" = "tick"): THREE.Sprite {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return new THREE.Sprite(new THREE.SpriteMaterial({ color: "#b9c7be" }));
  const fontSize = 40;
  context.font = `700 ${fontSize}px Inter, system-ui, sans-serif`;
  const metrics = context.measureText(text);
  canvas.width = Math.ceil(metrics.width + 36);
  canvas.height = fontSize + 20;
  context.font = `700 ${fontSize}px Inter, system-ui, sans-serif`;
  context.textBaseline = "middle";
  context.textAlign = "center";
  context.fillStyle = tone === "axis" ? "rgba(28, 47, 65, 0.82)" : "rgba(20, 37, 61, 0.86)";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = tone === "axis" ? "#d9f1ff" : "#d8e5ff";
  context.fillText(text, canvas.width / 2, canvas.height / 2);
  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  }));
  const height = panelCount > 1 ? 0.105 : 0.13;
  sprite.scale.set(height * (canvas.width / canvas.height), height, 1);
  sprite.renderOrder = 20;
  return sprite;
}
function addLine(group: THREE.Group, points: THREE.Vector3[], color: string, opacity = 0.86): void {
  group.add(new THREE.Line(
    new THREE.BufferGeometry().setFromPoints(points),
    new THREE.LineBasicMaterial({ color, transparent: opacity < 1, opacity, depthTest: false }),
  ));
}
function axisTicks(min: number, max: number) {
  return [
    { fraction: 0, label: formatNumber(min) },
    { fraction: 0.5, label: formatNumber((min + max) / 2) },
    { fraction: 1, label: formatNumber(max) },
  ];
}
function addAxisFrame(
  group: THREE.Group,
  data: MannequinSurfaceResponse,
  output: string,
  zRange: [number, number],
  metadata: MannequinMetadata,
  panelCount: number,
): void {
  const w = LAYOUT.width;
  const d = LAYOUT.depth;
  const zBase = -LAYOUT.lift / 2 - 0.06;
  const zTop = LAYOUT.lift / 2 + 0.06;
  const x0 = -w / 2;
  const y0 = -d / 2;
  addLine(group, [new THREE.Vector3(x0, y0, zBase), new THREE.Vector3(w / 2, y0, zBase)], "#72807d");
  addLine(group, [new THREE.Vector3(x0, y0, zBase), new THREE.Vector3(x0, d / 2, zBase)], "#72807d");
  addLine(group, [new THREE.Vector3(x0, y0, zBase), new THREE.Vector3(x0, y0, zTop)], "#84908d");
  const tick = panelCount > 1 ? 0.045 : 0.065;
  for (const item of axisTicks(data.x_range[0], data.x_range[1])) {
    const x = x0 + item.fraction * w;
    addLine(group, [new THREE.Vector3(x, y0 - tick, zBase), new THREE.Vector3(x, y0 + tick, zBase)], "#63706d", 0.7);
  }
  for (const item of axisTicks(data.y_range[0], data.y_range[1])) {
    const y = y0 + item.fraction * d;
    addLine(group, [new THREE.Vector3(x0 - tick, y, zBase), new THREE.Vector3(x0 + tick, y, zBase)], "#63706d", 0.7);
  }
  for (const item of axisTicks(zRange[0], zRange[1])) {
    const z = zBase + item.fraction * (zTop - zBase);
    addLine(group, [new THREE.Vector3(x0 - tick, y0, z), new THREE.Vector3(x0 + tick, y0, z)], "#63706d", 0.72);
  }
  const xUnit = metadata.input_config[data.x_axis]?.unit;
  const yUnit = metadata.input_config[data.y_axis]?.unit;
  const xDefinition = tailorMannequinAxis(data.x_axis);
  const yDefinition = tailorMannequinAxis(data.y_axis);
  const xLabel = xDefinition ? `${xDefinition.shortLabel} · ${xDefinition.label}` : data.x_axis;
  const yLabel = yDefinition ? `${yDefinition.shortLabel} · ${yDefinition.label}` : data.y_axis;
  const labels: Array<[string, THREE.Vector3, "axis" | "tick"]> = [
    [`${xLabel}${xUnit ? ` (${xUnit})` : ""}`, new THREE.Vector3(0, y0 - 0.31, zBase), "axis"],
    [`${yLabel}${yUnit ? ` (${yUnit})` : ""}`, new THREE.Vector3(x0 - 0.42, 0, zBase), "axis"],
    [output, new THREE.Vector3(x0 - 0.28, y0 - 0.04, zTop + 0.06), "axis"],
  ];
  for (const [text, position, tone] of labels) {
    const sprite = makeTextSprite(text, panelCount, tone);
    sprite.position.copy(position);
    group.add(sprite);
  }
}
function addZeroPlane(group: THREE.Group, zRange: [number, number]): void {
  if (zRange[0] > 0 || zRange[1] < 0) return;
  const zero = new THREE.Mesh(
    new THREE.PlaneGeometry(LAYOUT.width * 1.03, LAYOUT.depth * 1.15),
    new THREE.MeshBasicMaterial({
      color: "#b8c2cb",
      transparent: true,
      opacity: 0.22,
      side: THREE.DoubleSide,
      depthWrite: false,
    }),
  );
  zero.position.z = zForValue(0, zRange) + 0.004;
  zero.renderOrder = 2;
  group.add(zero);
}
function surfacePoint(data: MannequinSurfaceResponse, x: number, y: number, z: number, zRange: [number, number]) {
  return new THREE.Vector3(
    -LAYOUT.width / 2 + fractionInRange(x, data.x_range[0], data.x_range[1]) * LAYOUT.width,
    -LAYOUT.depth / 2 + fractionInRange(y, data.y_range[0], data.y_range[1]) * LAYOUT.depth,
    zForValue(finiteOrFallback(z, zRange[0]), zRange),
  );
}

const MAX_UNCERTAINTY_CLOUDS = 320;
const CLOUD_PARTICLES_PER_POINT = 160;

function deterministicUnit(seed: number): number {
  const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return value - Math.floor(value);
}


function deterministicNormal(seed: number): number {
  const u1 = Math.max(1e-6, deterministicUnit(seed));
  const u2 = deterministicUnit(seed + 1);
  return Math.max(-2.7, Math.min(2.7, Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)));
}

function addUncertaintyCloud(
  group: THREE.Group,
  data: MannequinSurfaceResponse,
  overlay: MannequinOverlay,
  zRange: [number, number],
  color: THREE.Color,
): void {
  const hasMc = Boolean(overlay.mc?.some((members) => members?.length));
  if (!hasMc && !overlay.sigma?.some((sigma) => sigma > 0)) return;
  // The stride budget applies ONLY to the synthetic sigma fuzz (160 particles
  // per point). Monte-Carlo member clouds ARE the transported points — every
  // one must draw, or evidence silently vanishes; their per-view member
  // budgets (16/24/48) already bound the particle count.
  const count = Math.min(overlay.x.length, MAX_UNCERTAINTY_CLOUDS);
  const stride = Math.max(1, Math.ceil(overlay.x.length / count));
  const positions: number[] = [];
  for (let pointIndex = 0; pointIndex < overlay.x.length; pointIndex += 1) {
    // Alpha/beta are measured coordinates, not uncertain inputs. Keep only a
    // tiny render-separation jitter here; the real N-D displacement is drawn in
    // the held-axis transport projection below the surface.
    const spreadX = 0.018;
    const spreadY = 0.014;
    const centre = surfacePoint(data, overlay.x[pointIndex]!, overlay.y[pointIndex]!, overlay.z[pointIndex]!, zRange);
    const members = overlay.mc?.[pointIndex];
    if (members?.length) {
      // REAL Monte-Carlo ensemble members: this cloud IS the transported
      // point (the mean marker collapses to an invisible pick anchor). No
      // synthetic Gaussian — each particle is one member's transported value.
      for (let member = 0; member < members.length; member += 1) {
        const seed = (pointIndex + 1) * 101 + member * 17;
        const normalX = deterministicNormal(seed + 7);
        const normalY = deterministicNormal(seed + 13);
        const z = surfacePoint(data, overlay.x[pointIndex]!, overlay.y[pointIndex]!, members[member]!, zRange).z;
        positions.push(centre.x + normalX * spreadX, centre.y + normalY * spreadY, z + 0.06);
      }
      continue;
    }
    if (pointIndex % stride !== 0) continue;
    const sigma = overlay.sigma?.[pointIndex] ?? 0;
    if (!(sigma > 0)) continue;
    for (let particle = 0; particle < CLOUD_PARTICLES_PER_POINT; particle += 1) {
      const seed = (pointIndex + 1) * 101 + particle * 17;
      const normalZ = deterministicNormal(seed);
      const normalX = deterministicNormal(seed + 7);
      const normalY = deterministicNormal(seed + 13);
      const z = surfacePoint(data, overlay.x[pointIndex]!, overlay.y[pointIndex]!, overlay.z[pointIndex]! + normalZ * sigma, zRange).z;
      positions.push(centre.x + normalX * spreadX, centre.y + normalY * spreadY, z + 0.06);
    }
  }
  if (!positions.length) return;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  const cloud = new THREE.Points(
    geometry,
    new THREE.PointsMaterial({
      color,
      // Member clouds carry ~24 real samples where the synthetic fuzz used
      // 160 particles — bolder dots keep the density readable at panel scale.
      size: hasMc ? 0.135 : 0.105,
      transparent: true,
      opacity: hasMc ? 0.5 : 0.31,
      depthWrite: false,
      sizeAttenuation: true,
    }),
  );
  cloud.renderOrder = 5;
  group.add(cloud);
}
function addOverlays(
  group: THREE.Group,
  data: MannequinSurfaceResponse,
  surface: MannequinOutputSurface,
  zRange: [number, number],
  layers: Partial<MannequinSurfaceLayerVisibility>,
): void {
  for (const overlay of surface.overlays) {
    const layer = overlayLayer(overlay);
    if (layer === "oracle-target" && layers.oracleTargets === false) continue;
    if (layer === "samples" && layers.samples === false) continue;
    if (overlay.mode === "lines") {
      const points = overlay.x.map((x, index) => surfacePoint(data, x, overlay.y[index]!, overlay.z[index]!, zRange));
      group.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(points),
        new THREE.LineBasicMaterial({ color: overlay.line?.color ?? "#f4b14a", transparent: true, opacity: overlay.line?.opacity ?? 0.94 }),
      ));
    } else {
      const color = new THREE.Color(overlay.marker?.color ?? "#50c8b8");
      addUncertaintyCloud(group, data, overlay, zRange, color);
      const radius = overlay.marker?.size ? Math.max(0.035, overlay.marker.size * 0.008) : 0.055;
      for (let index = 0; index < overlay.x.length; index += 1) {
        // A point with Monte-Carlo members is drawn as its cloud only: the
        // marker becomes a NON-RENDERED anchor that keeps right-click picking
        // (screen-space nearest over userData.pointId — traverse() visits
        // invisible children and matrixWorld stays current) at the mean.
        // visible=false, NOT opacity 0: a transparent mesh still writes depth
        // and would carve sphere-shaped holes in the member cloud behind it.
        const cloudOnly = Boolean(overlay.mc?.[index]?.length);
        const marker = new THREE.Mesh(
          new THREE.SphereGeometry(radius, 14, 8),
          new THREE.MeshBasicMaterial({ color, transparent: true, opacity: overlay.sigma ? 0.82 : 0.96 }),
        );
        marker.visible = !cloudOnly;
        marker.position.copy(surfacePoint(data, overlay.x[index]!, overlay.y[index]!, overlay.z[index]!, zRange));
        marker.position.z += 0.06;
        // Carries the SamplePoint id for right-click "Why this point?".
        marker.userData.pointId = overlay.ids?.[index] ?? null;
        group.add(marker);
      }
    }
  }
}
function applyView(panel: Panel, view: MannequinSurfaceViewState, panelCount: number): void {
  panel.group.rotation.set(view.pitch - 0.34, 0, view.yaw);
  panel.group.position.set(view.panX * 0.007, -view.panY * 0.007, 0);
  panel.group.scale.setScalar(view.zoom * (panelCount > 1 ? 0.66 : 0.9));
}
function clampView(view: MannequinSurfaceViewState): MannequinSurfaceViewState {
  return {
    yaw: view.yaw,
    pitch: Math.max(-Math.PI, Math.min(Math.PI, view.pitch)),
    zoom: Math.max(0.18, Math.min(8, view.zoom)),
    panX: Math.max(-220, Math.min(220, view.panX)),
    panY: Math.max(-180, Math.min(180, view.panY)),
  };
}
function setViewAttributes(root: HTMLElement | null, view: MannequinSurfaceViewState): void {
  if (!root) return;
  root.dataset.viewYaw = view.yaw.toFixed(4);
  root.dataset.viewPitch = view.pitch.toFixed(4);
  root.dataset.viewZoom = view.zoom.toFixed(4);
  root.dataset.viewPanX = view.panX.toFixed(2);
  root.dataset.viewPanY = view.panY.toFixed(2);
}
function disposeScene(scene: THREE.Scene): void {
  scene.traverse((object) => {
    const mesh = object as any;
    if (mesh.geometry) mesh.geometry.dispose();
    const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    for (const material of materials) {
      if (material.map) material.map.dispose();
      material.dispose();
    }
  });
  scene.clear();
}
function createPanel(
  data: MannequinSurfaceResponse,
  output: string,
  metadata: MannequinMetadata,
  panelCount: number,
  layers: Partial<MannequinSurfaceLayerVisibility>,
): Panel | null {
  const outputSurface = data.output_surfaces[output];
  if (!outputSurface) return null;
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, 1, 0.05, 80);
  camera.up.set(0, 0, 1);
  camera.position.set(0, -3.72, 2.42);
  camera.lookAt(0, 0, 0.1);
  scene.add(new THREE.HemisphereLight("#daeef4", "#1d2724", 1.62));
  const keyLight = new THREE.DirectionalLight("#ffffff", 1.7);
  keyLight.position.set(-2.4, -3.2, 4.2);
  scene.add(keyLight);
  const group = new THREE.Group();
  scene.add(group);
  const base = new THREE.Mesh(
    new THREE.PlaneGeometry(LAYOUT.width * 1.04, LAYOUT.depth * 1.16),
    new THREE.MeshBasicMaterial({ color: "#5d6670", transparent: true, opacity: 0.34, side: THREE.DoubleSide }),
  );
  base.position.z = -LAYOUT.lift / 2 - 0.12;
  group.add(base);
  const zRange = displayRange(outputSurface);
  const hasComparisons = outputSurface.comparison_surfaces?.some((comparison) => {
    const layer = comparisonLayer(comparison);
    return !layer || layers[layer] !== false;
  }) ?? false;
  addAxisFrame(group, data, output, zRange, metadata, panelCount);
  addZeroPlane(group, zRange);
  if (layers.estimate !== false) {
    addSurfaceMesh(group, data, outputSurface, zRange, hasComparisons ? 0.82 : 1, layers.uncertainty !== false);
    addWireGrid(group, data, outputSurface.z, zRange);
  }
  addComparisonSurfaces(group, data, outputSurface, zRange, layers);
  addOverlays(group, data, outputSurface, zRange, layers);
  const panel = { output, scene, camera, group };
  applyView(panel, DEFAULT_MANNEQUIN_VIEW, panelCount);
  return panel;
}
export default function MannequinSurfaceGrid({ metadata, outputs, surface, viewMode, viewState, onViewStateChange, layers = {}, onPointContextMenu }: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const panelsRef = useRef<Panel[]>([]);
  const viewRef = useRef<MannequinSurfaceViewState>({ ...(viewState ?? DEFAULT_MANNEQUIN_VIEW) });
  const onViewStateChangeRef = useRef<typeof onViewStateChange>(onViewStateChange);
  const onPointContextMenuRef = useRef<typeof onPointContextMenu>(onPointContextMenu);
  onPointContextMenuRef.current = onPointContextMenu;
  const dragRef = useRef<{ x: number; y: number; view: MannequinSurfaceViewState; mode: "rotate" | "pan" } | null>(null);
  const renderRef = useRef<() => void>(() => {});
  const outputSignature = useMemo(() => outputs.join("|"), [outputs]);
  const renderOutputs = useMemo(() => outputs, [outputSignature]);
  const layerSignature = `${layers.estimate !== false}|${layers.uncertainty !== false}|${layers.prior !== false}|${layers.truth !== false}|${layers.oracleTargets !== false}|${layers.samples !== false}`;
  const overlayLineCount = useMemo(() => renderOutputs.reduce((count, output) => {
    const outputSurface = surface.output_surfaces[output];
    return count + (outputSurface?.overlays.filter((overlay) => {
      const layer = overlayLayer(overlay);
      return overlay.mode === "lines"
        && (layer === "oracle-target" ? layers.oracleTargets !== false : layers.samples !== false);
    }).length ?? 0);
  }, 0), [layerSignature, renderOutputs, surface.output_surfaces]);
  useEffect(() => {
    onViewStateChangeRef.current = onViewStateChange;
  }, [onViewStateChange]);
  useEffect(() => {
    if (!viewState) return;
    viewRef.current = clampView(viewState);
    setViewAttributes(rootRef.current, viewRef.current);
    renderRef.current();
  }, [viewState?.panX, viewState?.panY, viewState?.pitch, viewState?.yaw, viewState?.zoom]);
  useEffect(() => {
    if (!canvasRef.current) return undefined;
    const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, alpha: true, antialias: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setScissorTest(true);
    rendererRef.current = renderer;
    const resizeObserver = new ResizeObserver(() => renderRef.current());
    if (rootRef.current) resizeObserver.observe(rootRef.current);
    return () => {
      resizeObserver.disconnect();
      renderer.dispose();
      rendererRef.current = null;
    };
  }, []);
  useEffect(() => {
    const panels = renderOutputs
      .map((output) => createPanel(surface, output, metadata, outputs.length, layers))
      .filter((panel): panel is Panel => Boolean(panel));
    panelsRef.current = panels;
    const render = () => {
      const renderer = rendererRef.current;
      const root = rootRef.current;
      if (!renderer || !root) return;
      const rootRect = root.getBoundingClientRect();
      const width = Math.max(1, Math.floor(rootRect.width));
      const height = Math.max(1, Math.floor(rootRect.height));
      setViewAttributes(root, viewRef.current);
      renderer.setSize(width, height, false);
      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, width, height);
      renderer.clear(true, true, true);
      renderer.setScissorTest(true);
      for (const panel of panelsRef.current) {
        const tile = root.querySelector<HTMLElement>(`[data-output="${panel.output}"]`);
        if (!tile) continue;
        const rect = tile.getBoundingClientRect();
        if (rect.width < 3 || rect.height < 3) continue;
        const vx = Math.max(0, Math.floor(rect.left - rootRect.left));
        const vy = Math.max(0, Math.floor(height - (rect.bottom - rootRect.top)));
        const vw = Math.max(1, Math.floor(rect.width));
        const vh = Math.max(1, Math.floor(rect.height));
        renderer.setViewport(vx, vy, vw, vh);
        renderer.setScissor(vx, vy, vw, vh);
        panel.camera.aspect = vw / vh;
        panel.camera.updateProjectionMatrix();
        applyView(panel, viewRef.current, panelsRef.current.length);
        renderer.render(panel.scene, panel.camera);
      }
    };
    renderRef.current = render;
    const frame = window.requestAnimationFrame(render);
    return () => {
      window.cancelAnimationFrame(frame);
      if (panelsRef.current === panels) panelsRef.current = [];
      panels.forEach((panel) => disposeScene(panel.scene));
    };
  }, [layerSignature, metadata, outputSignature, outputs.length, renderOutputs, surface]);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      // Right-click belongs to the per-point "Why this point?" menu; pan
      // stays available on shift-drag.
      if (event.button === 2) return;
      event.preventDefault();
      root.setPointerCapture(event.pointerId);
      dragRef.current = {
        x: event.clientX,
        y: event.clientY,
        view: { ...viewRef.current },
        mode: event.shiftKey ? "pan" : "rotate",
      };
    };
    const onPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      viewRef.current = drag.mode === "pan"
        ? clampView({ ...drag.view, panX: drag.view.panX + dx * 0.82, panY: drag.view.panY + dy * 0.82 })
        : clampView({ ...drag.view, yaw: drag.view.yaw + dx * 0.012, pitch: drag.view.pitch + dy * 0.011 });
      setViewAttributes(root, viewRef.current);
      onViewStateChangeRef.current?.({ ...viewRef.current });
      renderRef.current();
    };
    const finishDrag = (event: PointerEvent) => {
      dragRef.current = null;
      if (root.hasPointerCapture(event.pointerId)) root.releasePointerCapture(event.pointerId);
    };
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      viewRef.current = clampView({ ...viewRef.current, zoom: viewRef.current.zoom * Math.exp(-event.deltaY * 0.0012) });
      setViewAttributes(root, viewRef.current);
      onViewStateChangeRef.current?.({ ...viewRef.current });
      renderRef.current();
    };
    const reset = () => {
      viewRef.current = { ...DEFAULT_MANNEQUIN_VIEW };
      setViewAttributes(root, viewRef.current);
      onViewStateChangeRef.current?.({ ...viewRef.current });
      renderRef.current();
    };
    const preventMenu = (event: MouseEvent) => {
      event.preventDefault();
      const callback = onPointContextMenuRef.current;
      if (!callback) return;
      // Screen-space nearest marker carrying a pointId, inside whichever
      // panel was clicked — the spheres are small targets, so nearest-within-
      // radius beats a strict raycast.
      for (const panel of panelsRef.current) {
        const tile = root.querySelector<HTMLElement>(`[data-output="${panel.output}"]`);
        if (!tile) continue;
        const rect = tile.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right) continue;
        if (event.clientY < rect.top || event.clientY > rect.bottom) continue;
        panel.camera.aspect = rect.width / Math.max(1, rect.height);
        panel.camera.updateProjectionMatrix();
        applyView(panel, viewRef.current, panelsRef.current.length);
        panel.group.updateMatrixWorld(true);
        const position = new THREE.Vector3();
        let bestId: string | null = null;
        let bestDistance = 14; // px pick radius
        panel.group.traverse((child) => {
          const pointId = (child.userData as { pointId?: string | null }).pointId;
          if (!pointId) return;
          position.setFromMatrixPosition(child.matrixWorld);
          position.project(panel.camera);
          const screenX = rect.left + ((position.x + 1) / 2) * rect.width;
          const screenY = rect.top + ((1 - position.y) / 2) * rect.height;
          const distance = Math.hypot(screenX - event.clientX, screenY - event.clientY);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestId = pointId;
          }
        });
        if (bestId) callback(bestId, event.clientX, event.clientY);
        return;
      }
    };
    root.addEventListener("pointerdown", onPointerDown);
    root.addEventListener("pointermove", onPointerMove);
    root.addEventListener("pointerup", finishDrag);
    root.addEventListener("pointercancel", finishDrag);
    root.addEventListener("wheel", onWheel, { passive: false });
    root.addEventListener("dblclick", reset);
    root.addEventListener("contextmenu", preventMenu);
    return () => {
      root.removeEventListener("pointerdown", onPointerDown);
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerup", finishDrag);
      root.removeEventListener("pointercancel", finishDrag);
      root.removeEventListener("wheel", onWheel);
      root.removeEventListener("dblclick", reset);
      root.removeEventListener("contextmenu", preventMenu);
    };
  }, []);
  return (
    <div
      className={`three-surface-root ${viewMode}`}
      ref={rootRef}
      data-testid="mannequin-three-root"
      data-overlay-lines={overlayLineCount}
      title="Drag to rotate freely. Shift/right-drag to pan. Wheel or trackpad to zoom. Double-click to reset view."
    >
      <canvas className="three-surface-canvas" ref={canvasRef} />
      <div className={`three-panel-overlays ${viewMode}`}>
        {outputs.map((output) => {
          const outputSurface = surface.output_surfaces[output];
          return (
            <article className={`three-surface-panel ${viewMode}`} data-output={output} key={output}>
              <div className="three-panel-header">
                <strong>{output}</strong>
                <span>{metadata.output_labels[output] ?? output}</span>
              </div>
              <div className="three-panel-footer">
                {layers.uncertainty !== false && outputSurface?.colour_by === "uncertainty" ? (
                  <>
                    <span>σ {formatNumber(outputSurface.colour_range?.[0])}</span>
                    <span>certain → uncertain</span>
                    <span>σ {formatNumber(outputSurface.colour_range?.[1])}</span>
                  </>
                ) : (
                  <>
                    <span>{formatNumber(outputSurface?.z_range[0])}</span>
                    <span>mean {formatNumber(outputSurface?.stats.mean)}</span>
                    <span>{formatNumber(outputSurface?.z_range[1])}</span>
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
